import { useEffect, useState } from 'react'
import { CategorySettings } from '../components/CategorySettings'
import { ADMIN_PREFIX } from '../lib/adminRoute'
import { navigate } from '../lib/pathname'
import { api, type Tier } from '../lib/api'
import { M_PER_MILE, getLocationProvider, type PermissionState } from '../lib/location'
import { enablePush, notificationSupport } from '../lib/notify'
import { FREE_LIMIT, activeCount, useStore } from '../lib/store'
import './Settings.css'

const TIERS: { key: Tier; label: string; price: string; blurb: string }[] = [
  { key: 'free', label: 'Free', price: '$0', blurb: `${FREE_LIMIT} active reminders` },
  { key: 'pro', label: 'Pro', price: '$3.99/mo', blurb: 'Unlimited reminders' },
  { key: 'family', label: 'Family', price: '$6.99/mo', blurb: 'Shared household board' },
]

export function Settings() {
  const {
    user,
    reminders,
    watching,
    startWatching,
    stopWatching,
    signOut,
    refresh,
    locationError,
  } = useStore()
  const [radius, setRadius] = useState(user?.default_radius_m ?? 3219)
  const [note, setNote] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [pushState, setPushState] = useState(notificationSupport())
  const [geoState, setGeoState] = useState<PermissionState | null>(null)
  const provider = getLocationProvider()

  useEffect(() => {
    if (user) setRadius(user.default_radius_m)
  }, [user])

  // Location permission is the one setting the app cannot change for the user,
  // so the least it can do is say which way it currently sits. `getPermission()`
  // existed but had no callers, which is why a blocked browser showed nothing
  // more useful than the raw geolocation error string (APP-12).
  useEffect(() => {
    let live = true
    void provider.getPermission().then((p) => live && setGeoState(p))
    return () => {
      live = false
    }
  }, [provider, watching, locationError])

  /** Ask for a fix from a real click, which is the only reliable way to raise a
   * permission prompt — particularly on desktop Safari. */
  async function enableLocation() {
    setError(null)
    try {
      await provider.getCurrent()
      startWatching()
      setNote('Location enabled.')
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setGeoState(await provider.getPermission())
    }
  }

  async function setTier(tier: Tier) {
    setError(null)
    try {
      await api.updateMe({ tier })
      await useStore.getState().boot()
      await refresh()
      setNote(`Switched to ${tier}. No billing is wired up — this is a demo switch.`)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  async function saveRadius(value: number) {
    setRadius(value)
    try {
      await api.updateMe({ default_radius_m: value })
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  async function turnOnPush() {
    setError(null)
    setNote(null)
    const res = await enablePush()
    setPushState(notificationSupport())
    if (res.ok) setNote('Push enabled on this device.')
    else setError(res.reason ?? 'Could not enable push.')
  }

  const active = activeCount(reminders)

  return (
    <>
      <header className="nav">
        <h1>Profile</h1>
      </header>

      <div className="scroll">
        {error && <p className="banner banner--error" role="alert">{error}</p>}
        {note && <p className="banner" role="status">{note}</p>}

        <div className="set__head glass rise">
          <div className="avatar avatar--lg">
            {(user?.display_name ?? '?').slice(0, 1).toUpperCase()}
          </div>
          <div>
            <strong>{user?.display_name}</strong>
            <span>{user?.email}</span>
            <span className={`set__tier set__tier--${user?.tier}`}>
              {user?.tier?.toUpperCase()}
            </span>
          </div>
        </div>

        <h2 className="section-label">
          Plan <span className="section-label__value">{active} active</span>
        </h2>
        <div className="set__tiers">
          {TIERS.map((t) => (
            <button
              key={t.key}
              className={`set__plan glass${user?.tier === t.key ? ' is-on' : ''}`}
              onClick={() => setTier(t.key)}
            >
              <strong>{t.label}</strong>
              <span className="set__price">{t.price}</span>
              <span className="set__blurb">{t.blurb}</span>
            </button>
          ))}
        </div>
        <p className="hint">
          No payment processing in this build — the switch exists so the free-tier gate
          can be demonstrated.
        </p>

        <h2 className="section-label">
          Default radius
          <span className="section-label__value">
            {(radius / M_PER_MILE).toFixed(radius / M_PER_MILE < 1 ? 2 : 1)} mi
          </span>
        </h2>
        <input
          className="slider"
          type="range"
          min={402}
          max={16093}
          step={80}
          value={radius}
          aria-label="Default trigger radius"
          onChange={(e) => saveRadius(Number(e.target.value))}
        />
        <div className="slider__scale">
          <span>0.25 mi</span>
          <span>10 mi</span>
        </div>

        <CategorySettings />

        <h2 className="section-label">Location</h2>
        <div className="set__row glass">
          <div>
            <strong>Live tracking</strong>
            <span>{watching ? 'Watching your position' : 'Off — nothing can trigger'}</span>
          </div>
          <button
            role="switch"
            aria-checked={watching}
            aria-label="Live tracking"
            className="rc__switch"
            onClick={() => (watching ? stopWatching() : enableLocation())}
          >
            <span />
          </button>
        </div>
        {locationError && <p className="banner banner--error">{locationError}</p>}

        {geoState === 'denied' && (
          <p className="set__caveat">
            <strong>This browser is blocking location.</strong> Nudge can&rsquo;t
            re-ask once a site has been refused — open the padlock or location icon
            in the address bar, set Location to Allow, then reload. On macOS, also
            check System Settings &rsaquo; Privacy &amp; Security &rsaquo; Location
            Services.
          </p>
        )}
        {geoState === 'unsupported' && (
          <p className="set__caveat">
            <strong>No location support in this browser.</strong> Reminders still
            save and sync; they&rsquo;ll start triggering in a browser that has it.
          </p>
        )}
        {geoState === 'prompt' && !watching && (
          <p className="set__caveat">
            <strong>Location hasn&rsquo;t been granted yet.</strong> Turn on Live
            tracking above and your browser will ask.
          </p>
        )}

        {!provider.canRunInBackground && (
          <p className="set__caveat">
            <strong>Nudge only triggers while it&rsquo;s open.</strong> Browsers have no
            background geofencing — the standard was abandoned and never shipped, and iOS
            suspends web pages the moment you switch away. Household pings still arrive
            when closed because the server sends those. Full background triggering needs
            the native build.
          </p>
        )}

        <h2 className="section-label">Notifications</h2>
        <div className="set__row glass">
          <div>
            <strong>Push notifications</strong>
            <span>
              {pushState === 'granted'
                ? 'Allowed on this device'
                : pushState === 'denied'
                  ? 'Blocked — re-enable in browser settings'
                  : pushState === 'unsupported'
                    ? 'Not supported by this browser'
                    : 'Not enabled yet'}
            </span>
          </div>
          {pushState !== 'granted' && pushState !== 'unsupported' && (
            <button className="btn btn--ghost btn--sm" onClick={turnOnPush}>
              Enable
            </button>
          )}
        </div>

        {user?.is_admin && (
          <>
            <h2 className="section-label">Staff</h2>
            <a
              className="set__row set__row--link glass"
              href={ADMIN_PREFIX}
              onClick={(e) => {
                // Modifier-clicks stay real navigations so the console can be
                // opened in its own tab, which is how you actually use it.
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return
                e.preventDefault()
                navigate(ADMIN_PREFIX)
              }}
            >
              <div>
                <strong>Admin console</strong>
                <span>
                  {user.is_super_admin ? 'Super admin' : 'Admin'} — users, metrics, audit and
                  system health
                </span>
              </div>
              <svg
                className="set__chev"
                width="9"
                height="16"
                viewBox="0 0 9 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="m1.5 1.5 6 6.5-6 6.5" />
              </svg>
            </a>
          </>
        )}

        <button className="btn btn--ghost set__out" onClick={signOut}>
          Sign out
        </button>
      </div>
    </>
  )
}
